'use client'

import { AuthContext } from 'context/AuthContext'
import { isOsanoCmpActiveForCurrentUser } from 'lib/osanoCmpScriptSrc'
import { createContext, useContext, useEffect, useState } from 'react'

type OsanoCmpContextValue = {
  isActive: boolean
}

const OsanoCmpContext = createContext<OsanoCmpContextValue | null>(null)

export function OsanoCmpProvider({
  isActive,
  children,
}: {
  isActive: boolean
  children: React.ReactNode
}): JSX.Element {
  return (
    <OsanoCmpContext.Provider value={{ isActive }}>
      {children}
    </OsanoCmpContext.Provider>
  )
}

/**
 * Recomputes Osano activation when AuthContext session state changes.
 * Must render under AuthContextWrapper. Pass `initialIsActive` from
 * request-derived SSR (App Router) to avoid a false→true flash.
 */
export function OsanoCmpProviderSyncedToAuth({
  children,
  initialIsActive = false,
}: {
  children: React.ReactNode
  initialIsActive?: boolean
}): JSX.Element {
  const [authState] = useContext(AuthContext)
  const [isActive, setIsActive] = useState(initialIsActive)
  const permissionsKey = (authState?.permissions ?? []).join(',')

  useEffect(() => {
    setIsActive(isOsanoCmpActiveForCurrentUser())
  }, [authState?.isAuthenticated, authState?.isLoading, permissionsKey])

  return <OsanoCmpProvider isActive={isActive}>{children}</OsanoCmpProvider>
}

/**
 * Prefer request-derived activation from OsanoCmpProvider when present.
 * Falls back to the client token check (SSR-safe: false without window).
 */
export function useIsOsanoCmpActive(): boolean {
  const ctx = useContext(OsanoCmpContext)

  if (ctx) {
    return ctx.isActive
  }

  return isOsanoCmpActiveForCurrentUser()
}

export default OsanoCmpContext
